import React, { ChangeEvent, useState } from "react";
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface SecurityDialogProps {
  /**
   * An error message to be displayed when the security check fails.  This is most commonly 
   * determined by a HTTP 403 error on the request using this security check.
   */
  errorMessage?: string;
  /**
   * If `true`, the component is shown.
   * @default false
   */
  isOpen: boolean;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;
  /**
   * Callback fired when the component requests password submission.
   */
  onSubmit: (currentPassword: string) => void;
}

export default function SecurityDialog(props: SecurityDialogProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [errorMessage] = useState(props.errorMessage || "");

  function toggleShowCurrentPassword(): void {
    setShowCurrentPassword((current) => !current);
  };

  function handleChangeCurrentPassword(event: ChangeEvent<HTMLInputElement>): void {
    setCurrentPassword(event.target.value);
  }

  function handleCancel(): void {
    props.onClose();
  }

  function handleContinue(): void {
    props.onSubmit(currentPassword);
  }

  return (
    <Dialog open={props.isOpen}>
      <DialogTitle>
        <Typography variant="h3">{i18n.t("security-dialog.title")}</Typography>
      </DialogTitle>
      <IconButton onClick={handleCancel} sx={{position: "absolute", right: 8, top: 8}}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography variant="body1">{i18n.t("security-dialog.description")}</Typography>
        <Typography variant="body1">{i18n.t("security-dialog.instructions")}</Typography>
        <Stack spacing={1} sx={{
          marginTop: 2
        }}>
          <Box>{i18n.t("security-dialog.current-password")}</Box>
          <Stack direction="row" spacing={1}>
            <TextField fullWidth type={showCurrentPassword ? "text" : "password"} label="" autoComplete="off" size="small" value={currentPassword} onChange={handleChangeCurrentPassword} />
            <IconButton onClick={toggleShowCurrentPassword}>
              {showCurrentPassword && <VisibilityIcon />}
              {!showCurrentPassword && <VisibilityOffIcon />}
            </IconButton>
          </Stack>
          {errorMessage && <Alert variant="filled" severity="error">{errorMessage}</Alert>}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: "end",
            marginTop: 2
          }}>
          <Button variant="contained" color="secondary" onClick={handleCancel}>{i18n.t("security-dialog.cancel")}</Button>
          <Button disabled={currentPassword.length === 0} variant="contained" onClick={handleContinue}>Continue</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}